import React from 'react';

// The public Privacy Policy and Terms of Use (which double as the End-User
// Licence Agreement Intuit asks for before issuing QuickBooks production
// keys). The business's name, ABN, email and state come from Company
// Settings via the site profile; the wording is here. The Terms &
// Conditions of Trade aren't here: they're for Customers only, so the
// customer portal shows them (backend/content/legal).
const LAST_UPDATED = '5 October 2026';
const TERMS_OF_USE_UPDATED = '7 October 2026';

const STATE_NAMES = {
  NSW: 'New South Wales',
  VIC: 'Victoria',
  QLD: 'Queensland',
  SA: 'South Australia',
  WA: 'Western Australia',
  TAS: 'Tasmania',
  NT: 'the Northern Territory',
  ACT: 'the Australian Capital Territory',
};

// "71624730847" → "71 624 730 847".
export function formatAbn(abn) {
  const digits = String(abn || '').replace(/\D/g, '');
  return digits.length === 11 ? `${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5, 8)} ${digits.slice(8)}` : abn || '';
}

function useLegalParty(profile) {
  return {
    name: profile?.company_name || 'the business',
    abn: formatAbn(profile?.abn),
    email: profile?.email || '',
    state: STATE_NAMES[profile?.state] || 'New South Wales',
  };
}

function Email({ email }) {
  return email ? <a href={`mailto:${email}`}>{email}</a> : 'our contact email';
}

function LegalShell({ eyebrow, title, updated = LAST_UPDATED, children }) {
  return (
    <section className="ks-section">
      <div className="ks-wrap">
        <div className="ks-legal">
          <p className="ks-eyebrow">{eyebrow}</p>
          <h1 className="ks-display" style={{ maxWidth: 'none' }}>{title}</h1>
          <p className="ks-legal-meta">Last updated {updated}</p>
          {children}
        </div>
      </div>
    </section>
  );
}

export function PrivacyPage({ profile }) {
  const party = useLegalParty(profile);
  return (
    <LegalShell eyebrow="Legal" title="Privacy Policy">
      <p className="ks-lead" style={{ marginTop: 32 }}>
        {party.name}{party.abn ? ` (ABN ${party.abn})` : ''} (“we”, “us”) respects your privacy. This policy explains how we handle
        personal information through this website, our Customer portal and our business systems (together, the “Platform”),
        in line with the Privacy Act 1988 (Cth) and the Australian Privacy Principles.
      </p>

      <h2>Information we collect</h2>
      <ul>
        <li>Business contact details — names, business names, ABNs, email addresses, phone numbers and delivery and billing addresses.</li>
        <li>Account details for people who sign in to the Platform, such as login email and an encrypted password.</li>
        <li>Records of quotes, orders, deliveries, invoices and payments, and our correspondence with you.</li>
        <li>Basic technical information needed to keep you signed in and the Platform secure.</li>
      </ul>
      <p>We collect this information directly from you or your business, and from our own dealings with you.</p>

      <h2>How we use it</h2>
      <ul>
        <li>To set up and manage Trading Accounts and Customer portal access.</li>
        <li>To prepare quotes, process and deliver orders, and invoice and collect payment.</li>
        <li>To provide support and tell you about your account, orders and our products.</li>
        <li>To keep accurate business and accounting records, and to meet our legal obligations.</li>
      </ul>

      <h2>Accounting software (QuickBooks Online)</h2>
      <p>
        We keep our accounts in QuickBooks Online, provided by Intuit. When our Platform is connected to our own QuickBooks
        company, it reads and records accounting information there — such as products and items, customers, invoices and
        payments — so that our orders and our accounts match. This connection is used only for our own business records.
        It can be disconnected at any time from either the Platform or QuickBooks. Intuit handles that information under its own
        privacy statement.
      </p>

      <h2>Who we share it with</h2>
      <p>We don’t sell personal information. We share it only with service providers who help us run our business, including:</p>
      <ul>
        <li>hosting and email providers that run the Platform;</li>
        <li>our accounting software provider (Intuit QuickBooks Online);</li>
        <li>delivery carriers, such as Australia Post, to get your orders to you;</li>
        <li>our professional advisers, and government bodies where the law requires it.</li>
      </ul>
      <p>
        Our Platform is hosted in Singapore, and some of our providers store information elsewhere outside Australia, including in the United States. We take reasonable steps to
        make sure they protect it.
      </p>

      <h2>Cookies and browser storage</h2>
      <p>
        The Platform uses your browser’s storage only to keep you signed in. We don’t use advertising or tracking cookies.
      </p>

      <h2>Security</h2>
      <p>
        We protect personal information with access controls, encrypted connections and encrypted passwords, and we limit access
        to the people who need it.
      </p>

      <h2>Access, correction and complaints</h2>
      <p>
        You can ask to see or correct the personal information we hold about you, or raise a privacy concern, by emailing{' '}
        <Email email={party.email} />. We’ll respond within 30 days. If you’re not satisfied with our response, you can contact
        the Office of the Australian Information Commissioner at <a href="https://www.oaic.gov.au" target="_blank" rel="noopener noreferrer">oaic.gov.au</a>.
      </p>

      <h2>Changes to this policy</h2>
      <p>We may update this policy from time to time. The current version is always on this page.</p>
    </LegalShell>
  );
}

export function TermsPage({ profile }) {
  const party = useLegalParty(profile);
  return (
    <LegalShell eyebrow="Legal" title="Terms of Use" updated={TERMS_OF_USE_UPDATED}>
      <p className="ks-lead" style={{ marginTop: 32 }}>
        These terms, including the end-user licence below, apply to your use of the website, Customer portal and business
        systems operated by {party.name}{party.abn ? ` (ABN ${party.abn})` : ''} (together, the “Platform”). By using the
        Platform you agree to them.
      </p>

      <h2>1. Licence to use the Platform</h2>
      <p>
        We grant you a limited, non-exclusive, non-transferable and revocable licence to use the Platform for your business’s
        dealings with us. You may not copy, modify, resell, reverse engineer or attempt to gain unauthorised access to any part
        of the Platform.
      </p>

      <h2>2. Accounts</h2>
      <p>
        Access to the Customer portal and back office is by invitation. Keep your login details secure and tell us promptly if
        you think someone else has used them. You’re responsible for activity under your login, and we may suspend or close
        access that is misused.
      </p>

      <h2>3. Quotes, orders and pricing</h2>
      <p>
        Prices shown in the Platform are for your Trading Account and exclude GST unless stated. Quotes are valid for the period
        shown on them. Submitting an order places it with us at the prices shown; it is binding once we accept it, subject to
        stock availability, and freight is an estimate until we confirm it. Supply of goods is governed by our Terms &amp;
        Conditions of Trade, which trade customers can read in the Customer portal, and the invoice for each order.
      </p>

      <h2>4. Third-party services</h2>
      <p>
        The Platform connects to services provided by others — including Intuit QuickBooks Online for our accounting. Those
        services are governed by their providers’ own terms. We are not affiliated with or endorsed by Intuit.
      </p>

      <h2>5. Intellectual property</h2>
      <p>
        The Platform and its content belong to us or our licensors. Product names, brands and images belong to their respective
        owners and are used to describe the products we supply.
      </p>

      <h2>6. Availability</h2>
      <p>
        We aim to keep the Platform available and accurate, but it may occasionally be unavailable for maintenance or for reasons
        outside our control, and information on it may contain errors, which we’ll correct when we become aware of them.
      </p>

      <h2>7. Liability</h2>
      <p>
        Nothing in these terms excludes rights you have under the Australian Consumer Law that can’t be excluded. To the extent
        the law allows, we aren’t liable for indirect or consequential loss arising from use of the Platform, and our liability
        for anything else is limited to resupplying the relevant goods or services or paying the cost of doing so.
      </p>

      <h2>8. Ending access</h2>
      <p>
        You can stop using the Platform at any time. We may suspend or end your access if you breach these terms or your trade
        account is closed.
      </p>

      <h2>9. Privacy</h2>
      <p>
        Our <a href="/privacy">Privacy Policy</a> explains how we handle personal information.
      </p>

      <h2>10. General</h2>
      <p>
        These terms are governed by the laws of {party.state}, Australia. We may update them from time to time; continuing to use
        the Platform after an update means you accept the updated terms. Questions? Email <Email email={party.email} />.
      </p>
    </LegalShell>
  );
}
